import { useRouter } from "next/router";
import { AuthorizedRevenueChart } from "./AuthorizedRevenueChart";
import { BrandDistributionChart } from "./BrandDistributionChart";
import { CapturedRevenueChart } from "./CapturedRevenueChart";
import { DailyDeclinesChart } from "./DailyDeclinesChart";
import { DeclineReasonsChart } from "./DeclineReasonsChart";
import { IssuingCountriesChart } from "./IssuingCountriesChart";
import { MethodsChart } from "./MethodsChart";
import { ProvidersChart } from "./ProvidersChart";
import { RefundedRevenueChart } from "./RefundedRevenueChart";
import { TopIssuersChart } from "./TopIssuersChart";
import { TotalTransactionsChart } from "./TotalTransactionsChart";
import { useMemo } from "react";
import { isSameDay } from "date-fns";

export function DatadogAnalytics({
  revenueSectionRef,
  declinesSectionRef,
  brandsSectionRef,
}: {
  revenueSectionRef?: React.RefObject<HTMLDivElement>;
  declinesSectionRef?: React.RefObject<HTMLDivElement>;
  brandsSectionRef?: React.RefObject<HTMLDivElement>;
} = {}) {
  const router = useRouter();
  const { start_date, end_date } = router.query;

  const isSameDayRange = useMemo(() => {
    if (!start_date || !end_date) return false;

    return isSameDay(
      new Date(start_date as string),
      new Date(end_date as string)
    );
  }, [start_date, end_date]);

  return (
    <div className="mt-6 flex flex-col gap-6">
      <section
        ref={revenueSectionRef}
        id="revenue-section"
        className="flex scroll-mt-[140px] flex-col gap-6 lg:scroll-mt-[100px]"
      >
        <SectionTitle>Revenue Overview</SectionTitle>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <ProvidersChart />
          <MethodsChart />
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="col-span-full">
            <TotalTransactionsChart isSameDayRange={isSameDayRange} />
          </div>
          <CapturedRevenueChart isSameDayRange={isSameDayRange} />
          <AuthorizedRevenueChart isSameDayRange={isSameDayRange} />
          <RefundedRevenueChart isSameDayRange={isSameDayRange} />
        </div>
      </section>

      <section
        ref={declinesSectionRef}
        id="declines-section"
        className="flex scroll-mt-[140px] flex-col gap-6 lg:scroll-mt-[100px]"
      >
        <SectionTitle>Decline overview</SectionTitle>
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <DeclineReasonsChart />
          <DailyDeclinesChart isSameDayRange={isSameDayRange} />
        </div>
      </section>

      <section
        ref={brandsSectionRef}
        id="brands-section"
        className="flex scroll-mt-[140px] flex-col gap-6 lg:scroll-mt-[100px]"
      >
        <SectionTitle>Brands overview</SectionTitle>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <BrandDistributionChart />
          <IssuingCountriesChart />
          <TopIssuersChart />
        </div>
      </section>
    </div>
  );
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg font-medium text-foundations-boldest">{children}</h2>
);
